<template>
    <div class="container">
        <div class="pt-5">
            <h1 class="h3">Settings</h1>
            <div class="py-2">
                <settings-menu></settings-menu>
            </div>
            <div class="row py-3 border-bottom">
                <div class="col-md-6 col-sm-12">
                    <h3 class="h3 text-primary">Integrations</h3>
                    <small>Connect Dukakit to Shopify and WooCommerce so orders, payments, and inventory stay in sync.</small>
                </div>
            </div>

            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border" role="status"><span class="sr-only">Loading...</span></div>
            </div>

            <div v-else-if="loadFailed" class="alert alert-danger">
                Could not load integration status. <a href="#" @click.prevent="load">Retry</a>.
            </div>

            <div v-else class="py-3">
                <div v-for="channel in channelKeys" :key="channel" class="card mb-3 integration-card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start flex-wrap">
                            <div class="d-flex align-items-start">
                                <div class="integration-icon mr-3" :class="integrations[channel].connected ? 'bg-primary text-white' : 'bg-light text-dark'">
                                    <i :class="['bi', channelIcon(channel)]"></i>
                                </div>
                                <div>
                                    <h5 class="mb-1">{{ integrations[channel].label }}</h5>
                                    <span v-if="integrations[channel].connected" class="badge badge-primary">
                                        <i class="bi bi-check-circle-fill mr-1"></i>Connected
                                    </span>
                                    <span v-else class="badge badge-secondary">Not connected</span>
                                    <div v-if="integrations[channel].connected" class="mt-2 text-muted">
                                        <div v-if="integrations[channel].shop_domain"><i class="bi bi-shop mr-1"></i>{{ integrations[channel].shop_domain }}</div>
                                        <div><i class="bi bi-arrow-repeat mr-1"></i>Last sync: {{ integrations[channel].last_sync_at ? formatDate(integrations[channel].last_sync_at) : 'Never' }}</div>
                                        <div><i class="bi bi-bag-check mr-1"></i>Orders synced: {{ integrations[channel].order_count }}</div>
                                        <div v-if="integrations[channel].last_sync_error" class="text-danger">
                                            <i class="bi bi-exclamation-triangle-fill mr-1"></i>Sync error: {{ integrations[channel].last_sync_error }}
                                        </div>
                                        <div v-if="integrations[channel].open_sync_issue_count > 0" class="text-dark font-weight-bold">
                                            <i class="bi bi-flag-fill mr-1"></i>{{ integrations[channel].open_sync_issue_count }} unresolved sync issue(s)
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="text-right mt-2 mt-md-0">
                                <template v-if="!integrations[channel].connected">
                                    <button class="btn btn-primary btn-sm" @click="openConnectModal(channel)">
                                        <i class="bi bi-plug mr-1"></i>Connect
                                    </button>
                                </template>
                                <template v-else>
                                    <button class="btn btn-outline-secondary btn-sm mr-1" :disabled="syncing === channel" @click="syncNow(channel)">
                                        <span v-if="syncing === channel" class="spinner-border spinner-border-sm mr-1"></span>
                                        <i v-else class="bi bi-arrow-repeat mr-1"></i>Sync Now
                                    </button>
                                    <button class="btn btn-outline-danger btn-sm" @click="confirmDisconnect(channel)">Disconnect</button>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ── BYOK: Bring Your Own OpenAI API Key ─────────────────── -->
                <div class="card mb-3 integration-card" :class="{ 'byok-locked': !byok.eligible }">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start flex-wrap">
                            <div class="d-flex align-items-start">
                                <div class="integration-icon mr-3" :class="byok.connected ? 'bg-primary text-white' : 'bg-light text-dark'">
                                    <i class="bi bi-key-fill"></i>
                                </div>
                                <div>
                                    <h5 class="mb-1">
                                        Bring Your Own OpenAI API Key
                                        <span class="badge badge-dark ml-1">PRO &middot; KSh {{ byok.required_plan_amount }}</span>
                                    </h5>
                                    <small class="text-muted d-block mb-1" style="max-width:520px;">
                                        Use your own OpenAI API key for dukakit AI and have OpenAI usage billed directly to your own OpenAI account.
                                        Available on the KSh {{ byok.required_plan_amount }} Pro plan and above.
                                    </small>
                                    <span v-if="byok.usable" class="badge badge-primary">
                                        <i class="bi bi-check-circle-fill mr-1"></i>OpenAI &middot; Connected &middot; Key: &bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;{{ byok.key_last_four }}
                                    </span>
                                    <span v-else-if="byok.connected && !byok.eligible" class="badge badge-dark">
                                        <i class="bi bi-exclamation-triangle-fill mr-1"></i>Connected key inactive &middot; requires Pro
                                    </span>
                                    <span v-else-if="!byok.eligible" class="badge badge-secondary">
                                        Pro Feature &middot; Available on Pro &mdash; KSh {{ byok.required_plan_amount }}/month
                                    </span>
                                    <span v-else class="badge badge-secondary">Not connected</span>
                                    <small v-if="byok.connected && !byok.eligible" class="text-muted d-block mt-1" style="max-width:420px;">
                                        Your plan dropped below Pro, so SharaGPT is using Shara AI (not your key) until you upgrade again. Your key is still saved - upgrade to resume using it, or disconnect it below.
                                    </small>
                                </div>
                            </div>
                            <div class="text-right mt-2 mt-md-0">
                                <a v-if="!byok.eligible" href="/settings?type=features" class="btn btn-primary btn-sm mb-1">Upgrade to Pro</a>
                                <button v-if="!byok.connected && byok.eligible" class="btn btn-primary btn-sm" @click="$refs.connectByokModal.show()">
                                    <i class="bi bi-plug mr-1"></i>Connect OpenAI
                                </button>
                                <button v-if="byok.connected" class="btn btn-outline-danger btn-sm d-block ml-auto" @click="$refs.disconnectByokModal.show()">Disconnect</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card mb-3">
                <div class="card-body">
                    <h6>Legacy API Key <span class="badge badge-secondary">Deprecated</span></h6>
                    <small class="text-muted d-block mb-2">
                        Only needed if your WooCommerce plugin was configured against the old <code>/api/external-orders</code> endpoint.
                        New connections should use WooCommerce above instead.
                    </small>
                    <span v-if="userDetails.api_token">{{ userDetails.api_token }}</span>
                    <div class="mt-2">
                        <a @click="generateToken" class="btn btn-sm btn-secondary">Generate new Key</a>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── Connect Shopify modal ─────────────────────────────────────── -->
        <b-modal ref="connectShopifyModal" hide-header hide-footer centered size="md" body-class="p-0">
            <div class="integration-modal-header text-white p-4 text-center">
                <div class="integration-modal-icon mb-2 text-primary"><i class="bi bi-shop"></i></div>
                <h4 class="mb-1 text-primary">Connect Shopify</h4>
                <p class="mb-0" style="opacity:.85">Bring your Shopify orders, inventory, and fulfillment into Dukakit.</p>
            </div>
            <div class="p-4">
                <ul class="list-unstyled mb-4">
                    <li class="mb-2"><i class="bi bi-check-circle-fill text-primary mr-2"></i>Orders sync automatically, paid or not</li>
                    <li class="mb-2"><i class="bi bi-check-circle-fill text-primary mr-2"></i>Inventory updates when you pack &amp; dispatch</li>
                    <li class="mb-0"><i class="bi bi-check-circle-fill text-primary mr-2"></i>One dashboard for POS + Shopify sales</li>
                </ul>
                <label class="font-weight-bold">Your Shopify store domain</label>
                <input type="text" class="form-control mb-1" v-model="shopifyStoreInput" placeholder="mystore or mystore.myshopify.com"
                       @keyup.enter="submitShopifyConnect" @input="customDomainWarning = false">

                <div v-if="customDomainWarning" class="alert alert-danger py-2 my-2 small">
                    <i class="bi bi-exclamation-triangle-fill mr-1"></i>
                    That looks like your <strong>custom domain</strong> ({{ lastCustomDomainDetected }}) — Shopify needs your store's
                    <code>.myshopify.com</code> address to connect an app, even if you use a custom domain for your storefront.
                </div>

                <small class="text-muted d-block mb-2">
                    You can type just your store name, or paste the full <code>.myshopify.com</code> address.
                </small>
                <a href="#" class="d-block mb-4" @click.prevent="showFindDomainHelp = !showFindDomainHelp">
                    <i class="bi" :class="showFindDomainHelp ? 'bi-chevron-up' : 'bi-question-circle'"></i>
                    Don't know your myshopify.com domain?
                </a>
                <div v-if="showFindDomainHelp" class="text-muted mb-4 pl-3 border-left">
                    Log into <a href="https://admin.shopify.com" target="_blank" rel="noopener">admin.shopify.com</a> — the address bar
                    will show <code>admin.shopify.com/store/&lt;yourstore&gt;</code>, and your domain is
                    <code>&lt;yourstore&gt;.myshopify.com</code>. Or in your Shopify admin, go to
                    <strong>Settings → Domains</strong> and look for the one Shopify manages for you.
                </div>

                <button class="btn btn-primary btn-block" :disabled="!shopifyStoreInput || connecting === 'shopify'" @click="submitShopifyConnect">
                    <span v-if="connecting === 'shopify'" class="spinner-border spinner-border-sm mr-1"></span>
                    Continue to Shopify
                </button>
                <button class="btn btn-link btn-block text-secondary" @click="$refs.connectShopifyModal.hide()">Cancel</button>
            </div>
        </b-modal>

        <!-- ── Connect WooCommerce modal ─────────────────────────────────── -->
        <b-modal ref="connectWooModal" hide-header hide-footer centered size="md" body-class="p-0">
            <div class="integration-modal-header bg-primary text-white p-4 text-center">
                <div class="integration-modal-icon mb-2"><i class="bi bi-cart3"></i></div>
                <h4 class="mb-1">Connect WooCommerce</h4>
                <p class="mb-0" style="opacity:.85">Bring your WooCommerce orders into Dukakit.</p>
            </div>
            <div class="p-4">
                <ul class="list-unstyled mb-4">
                    <li class="mb-2"><i class="bi bi-check-circle-fill text-primary mr-2"></i>Orders sync automatically, paid or not</li>
                    <li class="mb-2"><i class="bi bi-check-circle-fill text-primary mr-2"></i>Inventory updates when you pack &amp; dispatch</li>
                    <li class="mb-0"><i class="bi bi-check-circle-fill text-primary mr-2"></i>One dashboard for POS + WooCommerce sales</li>
                </ul>
                <label class="font-weight-bold">Your WooCommerce site URL</label>
                <input type="text" class="form-control mb-1" v-model="wooSiteUrl" placeholder="https://mystore.com"
                       @keyup.enter="connectWooCommerce">
                <small class="text-muted d-block mb-4">We'll generate a secure connection key for your Dukakit plugin — shown once, right after this step.</small>

                <button class="btn btn-primary btn-block" :disabled="!wooSiteUrl || connecting === 'woocommerce'" @click="connectWooCommerce">
                    <span v-if="connecting === 'woocommerce'" class="spinner-border spinner-border-sm mr-1"></span>
                    Connect WooCommerce
                </button>
                <button class="btn btn-link btn-block text-secondary" @click="$refs.connectWooModal.hide()">Cancel</button>
            </div>
        </b-modal>

        <!-- ── WooCommerce credentials (shown once) modal ────────────────── -->
        <b-modal ref="wooCredentialsModal" hide-header hide-footer centered size="md" no-close-on-backdrop no-close-on-esc body-class="p-0">
            <div class="integration-modal-header bg-primary text-white p-4 text-center">
                <div class="integration-modal-icon mb-2"><i class="bi bi-key-fill"></i></div>
                <h4 class="mb-1">WooCommerce Connected</h4>
                <p class="mb-0" style="opacity:.85">Copy these into your Dukakit WooCommerce plugin settings now.</p>
            </div>
            <div class="p-4" v-if="wooCredentials">
                <div class="alert alert-danger py-2 mb-3">
                    <i class="bi bi-exclamation-triangle-fill mr-1"></i>The secret below is shown <strong>only once</strong>. Copy it now.
                </div>

                <div class="credential-row mb-3">
                    <label class="font-weight-bold mb-1">Ingest URL</label>
                    <div class="input-group">
                        <input type="text" class="form-control form-control-sm" readonly :value="wooCredentials.ingest_url">
                        <div class="input-group-append">
                            <button class="btn btn-outline-secondary btn-sm" v-clipboard:copy="wooCredentials.ingest_url" v-clipboard:success="onCopy" v-clipboard:error="onCopyError">
                                <i class="bi bi-clipboard"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="credential-row mb-3">
                    <label class="font-weight-bold mb-1"><code>X-Dukakit-Connection</code> header</label>
                    <div class="input-group">
                        <input type="text" class="form-control form-control-sm" readonly :value="wooCredentials.connection_id">
                        <div class="input-group-append">
                            <button class="btn btn-outline-secondary btn-sm" v-clipboard:copy="wooCredentials.connection_id" v-clipboard:success="onCopy" v-clipboard:error="onCopyError">
                                <i class="bi bi-clipboard"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="credential-row mb-4">
                    <label class="font-weight-bold mb-1"><code>X-Dukakit-Signature</code> secret</label>
                    <div class="input-group">
                        <input type="text" class="form-control form-control-sm" readonly :value="wooCredentials.webhook_secret">
                        <div class="input-group-append">
                            <button class="btn btn-outline-secondary btn-sm" v-clipboard:copy="wooCredentials.webhook_secret" v-clipboard:success="onCopy" v-clipboard:error="onCopyError">
                                <i class="bi bi-clipboard"></i>
                            </button>
                        </div>
                    </div>
                    <small class="text-muted">HMAC-SHA256 the request body with this secret to produce the signature header.</small>
                </div>

                <button class="btn btn-primary btn-block" @click="$refs.wooCredentialsModal.hide()">I've copied these — Done</button>
            </div>
        </b-modal>

        <!-- Plan upgrade confirmation modal -->
        <b-modal ref="planUpgradeModal" title="Plan Upgrade Required" hide-footer centered>
            <div class="alert alert-primary">
                <strong>{{ pendingChannelLabel }} integration requires the KSh {{ pendingUpgrade.required_plan_amount }}/month plan.</strong>
            </div>
            <table class="table table-sm mb-3">
                <tbody>
                    <tr><td>Current plan</td><td class="text-right">KSh {{ pendingUpgrade.current_plan_amount }}/month</td></tr>
                    <tr><td>New plan</td><td class="text-right">KSh {{ pendingUpgrade.required_plan_amount }}/month</td></tr>
                </tbody>
            </table>
            <p class="text-muted">{{ pendingUpgrade.message }}</p>
            <div class="d-flex justify-content-end">
                <button class="btn btn-outline-secondary mr-2" @click="cancelUpgrade">Cancel</button>
                <button class="btn btn-primary" :disabled="connecting" @click="confirmUpgrade">I Agree &amp; Upgrade</button>
            </div>
        </b-modal>

        <b-modal ref="disconnectModal" title="Disconnect Integration" hide-footer centered>
            <p>Disconnect {{ pendingChannelLabel }}? Existing orders and history are kept — new orders will no longer sync.</p>
            <div class="d-flex justify-content-end">
                <button class="btn btn-outline-secondary mr-2" @click="$refs.disconnectModal.hide()">Cancel</button>
                <button class="btn btn-danger" @click="disconnect">Disconnect</button>
            </div>
        </b-modal>

        <!-- ── Connect OpenAI (BYOK) modal ──────────────────────────────────── -->
        <b-modal ref="connectByokModal" hide-header hide-footer centered size="md" body-class="p-0">
            <div class="integration-modal-header bg-dark text-white p-4 text-center">
                <div class="integration-modal-icon mb-2"><i class="bi bi-key-fill"></i></div>
                <h4 class="mb-1">Connect your OpenAI API key</h4>
            </div>
            <div class="p-4">
                <p class="text-muted small">
                    Use your own OpenAI API key to run SharaGPT using your OpenAI account. OpenAI will bill your account
                    directly for API usage. dukakit will not charge its 30% AI usage markup on requests made through your
                    connected key.
                </p>
                <p class="text-muted small">
                    Your key is stored securely by dukakit and is never displayed again after it has been saved. You can
                    disconnect it at any time. You must have an OpenAI account with API access and available API
                    billing/credits.
                </p>

                <label class="font-weight-bold">OpenAI API Key</label>
                <input
                    type="password"
                    class="form-control mb-2"
                    v-model="byokKeyInput"
                    placeholder="sk-..."
                    autocomplete="off"
                    @keyup.enter="submitByokConnect"
                >

                <div class="alert alert-danger py-2 my-2 small" v-if="byokError">{{ byokError }}</div>

                <small class="text-muted d-block mb-3">
                    <i class="bi bi-shield-lock mr-1"></i>Your API key is encrypted and used server-side only. Never share
                    an API key with another person.
                </small>

                <button class="btn btn-primary btn-block" :disabled="!byokKeyInput || connectingByok" @click="submitByokConnect">
                    <span v-if="connectingByok" class="spinner-border spinner-border-sm mr-1"></span>
                    Connect API Key
                </button>
                <button class="btn btn-link btn-block text-secondary" @click="$refs.connectByokModal.hide()">Cancel</button>
            </div>
        </b-modal>

        <b-modal ref="disconnectByokModal" title="Disconnect OpenAI API Key" hide-footer centered>
            <p>Disconnect your OpenAI API key? SharaGPT will go back to using dukakit's own AI balance for this business.</p>
            <div class="d-flex justify-content-end">
                <button class="btn btn-outline-secondary mr-2" @click="$refs.disconnectByokModal.hide()">Cancel</button>
                <button class="btn btn-danger" @click="disconnectByok">Disconnect</button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import {mapGetters} from 'vuex'
export default {
    name: "integrations",
    computed: {
        ...mapGetters(['userDetails']),
        pendingChannelLabel() {
            return this.pendingChannel === 'shopify' ? 'Shopify' : (this.pendingChannel === 'woocommerce' ? 'WooCommerce' : '')
        },
    },
    data() {
        return {
            loading: true,
            loadFailed: false,
            // Safe defaults so the template can never index into an
            // undefined channel, regardless of load timing/failure.
            integrations: {
                shopify: { label: 'Shopify', connected: false },
                woocommerce: { label: 'WooCommerce', connected: false },
            },
            channelKeys: ['shopify', 'woocommerce'],
            connecting: null,
            syncing: null,
            pendingChannel: null,
            pendingUpgrade: { current_plan_amount: 0, required_plan_amount: 0, message: '' },
            shopifyStoreInput: '',
            customDomainWarning: false,
            lastCustomDomainDetected: '',
            showFindDomainHelp: false,
            wooSiteUrl: '',
            wooCredentials: null,
            byok: { eligible: false, required_plan_amount: 3000, current_plan_amount: 0, connected: false, key_last_four: null },
            byokKeyInput: '',
            byokError: '',
            connectingByok: false,
        }
    },
    mounted() {
        this.load()
        this.loadByokStatus()
        const params = new URLSearchParams(window.location.search)
        if (params.get('shopify') === 'connected') {
            this.$toasts.push({ type: 'success', message: 'Shopify connected — initial sync started.' })
        }
    },
    methods: {
        formatDate(d) {
            return new Date(d).toLocaleString()
        },
        channelIcon(channel) {
            return channel === 'shopify' ? 'bi-shop' : 'bi-cart3'
        },
        generateToken() {
            axios.get('/api/generate-token').then(() => {
                this.$toasts.push({ type: 'success', message: 'Token generated successfully' })
                location.reload()
            })
        },
        load() {
            this.loading = true
            this.loadFailed = false
            axios.get('/api/integrations')
                .then(res => {
                    this.integrations = { ...this.integrations, ...res.data }
                })
                .catch(() => {
                    this.loadFailed = true
                })
                .finally(() => { this.loading = false })
        },
        onCopy() {
            this.$toasts.push({ type: 'success', message: 'Copied to clipboard' })
        },
        onCopyError() {
            this.$toasts.push({ type: 'error', message: 'Could not copy — please copy it manually' })
        },
        openConnectModal(channel) {
            this.pendingChannel = channel
            if (channel === 'shopify') {
                this.shopifyStoreInput = ''
                this.customDomainWarning = false
                this.showFindDomainHelp = false
                this.$refs.connectShopifyModal.show()
            } else {
                this.wooSiteUrl = ''
                this.$refs.connectWooModal.show()
            }
        },
        // Accepts a bare store name ("mystore"), a full myshopify.com
        // address, or a pasted URL — and detects when what was typed is
        // actually the store's *custom* domain, which Shopify's OAuth flow
        // never accepts (it always requires the *.myshopify.com address,
        // even for stores with a connected custom domain — confirmed
        // against current Shopify docs). Rather than send that to the
        // backend and let the regex reject it with a generic error, catch
        // it here with a specific, actionable explanation.
        submitShopifyConnect() {
            if (!this.shopifyStoreInput) return

            const raw = this.shopifyStoreInput.trim()
                .replace(/^https?:\/\//i, '')
                .replace(/^www\./i, '')
                .replace(/\/.*$/, '')
                .toLowerCase()

            let shopDomain
            if (raw.endsWith('.myshopify.com')) {
                shopDomain = raw
            } else if (!raw.includes('.')) {
                shopDomain = `${raw}.myshopify.com`
            } else {
                this.customDomainWarning = true
                this.lastCustomDomainDetected = raw
                return
            }

            this.customDomainWarning = false
            this.pendingChannel = 'shopify'
            this.enableFeatureThenConnect('shopify_enabled', () => {
                window.location.href = '/api/integrations/shopify/install?shop=' + encodeURIComponent(shopDomain)
            })
        },
        // Reuses the existing PlanEnforcementService confirm-then-upgrade
        // round trip (/api/settings/toggle-feature) — the same mechanism
        // used for payroll/KDS/etc, extended server-side to gate Shopify/
        // WooCommerce at the 1000 tier instead of the 3000 "advanced" tier.
        enableFeatureThenConnect(feature, onEnabled) {
            this.connecting = this.pendingChannel
            axios.post('/api/settings/toggle-feature', { feature, value: true, confirmed: false })
                .then(({ data }) => {
                    if (data.requires_confirmation) {
                        this.pendingUpgrade = {
                            current_plan_amount: data.current_plan_amount,
                            required_plan_amount: data.required_plan_amount,
                            message: data.message,
                        }
                        this.connecting = null
                        this.$refs.connectShopifyModal.hide()
                        this.$refs.connectWooModal.hide()
                        this.$refs.planUpgradeModal.show()
                        this._pendingOnEnabled = onEnabled
                        return
                    }
                    this.connecting = null
                    onEnabled()
                })
                .catch(err => {
                    this.connecting = null
                    this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not start connection.' })
                })
        },
        confirmUpgrade() {
            const feature = this.pendingChannel === 'shopify' ? 'shopify_enabled' : 'woocommerce_enabled'
            this.connecting = this.pendingChannel
            axios.post('/api/settings/toggle-feature', { feature, value: true, confirmed: true })
                .then(({ data }) => {
                    this.connecting = null
                    this.$refs.planUpgradeModal.hide()
                    if (data.blocked) {
                        this.$toasts.push({ type: 'error', message: data.message })
                        return
                    }
                    this.$toasts.push({ type: 'success', message: 'Plan upgraded.' })
                    if (this._pendingOnEnabled) this._pendingOnEnabled()
                })
                .catch(err => {
                    this.connecting = null
                    this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Upgrade failed.' })
                })
        },
        cancelUpgrade() {
            this.$refs.planUpgradeModal.hide()
            this.pendingChannel = null
            this._pendingOnEnabled = null
        },
        connectWooCommerce() {
            if (!this.wooSiteUrl) return
            this.pendingChannel = 'woocommerce'
            this.enableFeatureThenConnect('woocommerce_enabled', () => {
                this.connecting = 'woocommerce'
                axios.post('/api/integrations/woocommerce/connect', { site_url: this.wooSiteUrl })
                    .then(({ data }) => {
                        this.wooCredentials = data
                        this.$refs.connectWooModal.hide()
                        this.$refs.wooCredentialsModal.show()
                        this.load()
                    })
                    .catch(err => {
                        this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not connect WooCommerce.' })
                    })
                    .finally(() => { this.connecting = null })
            })
        },
        syncNow(channel) {
            this.syncing = channel
            axios.post(`/api/integrations/${channel}/sync-now`)
                .then(() => { this.$toasts.push({ type: 'success', message: 'Sync started — this runs in the background.' }) })
                .catch(err => { this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not start sync.' }) })
                .finally(() => { this.syncing = null; setTimeout(() => this.load(), 2000) })
        },
        confirmDisconnect(channel) {
            this.pendingChannel = channel
            this.$refs.disconnectModal.show()
        },
        disconnect() {
            axios.post(`/api/integrations/${this.pendingChannel}/disconnect`)
                .then(() => {
                    this.$toasts.push({ type: 'success', message: 'Disconnected.' })
                    this.$refs.disconnectModal.hide()
                    this.load()
                })
                .catch(err => { this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not disconnect.' }) })
        },

        // ── BYOK (Bring Your Own OpenAI API Key) ────────────────────────────
        loadByokStatus() {
            axios.get('/api/ai/byok')
                .then(res => { this.byok = res.data })
                .catch(() => {})
        },
        submitByokConnect() {
            if (!this.byokKeyInput) return
            this.connectingByok = true
            this.byokError = ''
            axios.post('/api/ai/byok/connect', { api_key: this.byokKeyInput })
                .then(res => {
                    this.byok = res.data
                    this.byokKeyInput = ''
                    this.$refs.connectByokModal.hide()
                    this.$toasts.push({ type: 'success', message: 'OpenAI API key connected.' })
                })
                .catch(err => {
                    this.byokError = err.response?.data?.message || "We couldn't connect this API key. Please verify the key and your OpenAI API billing/API access."
                })
                .finally(() => { this.connectingByok = false })
        },
        disconnectByok() {
            axios.post('/api/ai/byok/disconnect')
                .then(() => {
                    this.$refs.disconnectByokModal.hide()
                    this.loadByokStatus()
                    this.$toasts.push({ type: 'success', message: 'OpenAI API key disconnected.' })
                })
                .catch(err => { this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not disconnect.' }) })
        },
    },
}
</script>

<style scoped>
.integration-card {
    transition: box-shadow .15s ease;
    border: 1px solid rgba(0,0,0,.08);
}
.integration-card:hover {
    box-shadow: 0 .25rem .75rem rgba(0,0,0,.06);
}
.integration-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    flex-shrink: 0;
}
.integration-modal-header {
    border-top-left-radius: calc(.3rem - 1px);
    border-top-right-radius: calc(.3rem - 1px);
}
.integration-modal-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto;
    border-radius: 50%;
    background: rgba(255,255,255,.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
}
.credential-row input {
    font-family: monospace;
    font-size: .8rem;
}
</style>
